import { useMemo, useState } from 'react';
import type { StepProps } from '../OBWWizard';
import {
  RESIDENCY_HELPER,
  RESIDENCY_LABEL,
  closedGateLine,
  consequenceLine,
  countryName,
  countryOptions,
  createdInZoneNotice,
  sameCountryLine,
  zoneFor,
} from '../residency';

/**
 * StepResidency — "Which country is your business registered in?" (ADR-124 D1, I3, I9).
 * Common Catalogue copy-fork: canonical here, forked into `portal-staff/src/obw/steps/`.
 *
 * ⭐ THE ANSWER IS A COUNTRY; THE CONSEQUENCE IS A ZONE; THE CUSTOMER SEES BOTH. The select is
 * the one place a business declares where it is legally established, and the line beneath it
 * always states — as a fact, never a promise — where its data will be stored. The customer
 * never sees a region name.
 *
 * ⚠ TWO MODES, CHOSEN BY THE MOUNT'S DATA, NEVER BY SNIFFING THE MOUNT (ADR-076 L5):
 *   · `residency.stored` present → the home already exists (an account-bearing journey:
 *     `sign-up` after the public form, `manage-account`). It is immutable (D1), so this renders
 *     read-only — no field, nothing blocks Continue.
 *   · otherwise → an account-less journey that will mint from this answer. Editable until
 *     confirmed; Continue runs the mount's `confirm` port (staff: mint the launch token in the
 *     derived zone; BOW: that zone's beta-invites gate) and stops on the form when it refuses.
 *
 * ⛔ NO GEO DECISION. `prefill` (the visitor's `cf-ipcountry`) may pre-select a country and
 * nothing else — the customer still confirms it (ADR-120 Amendment 4; ADR-124 D1).
 *
 * ⛔ NO FETCH HERE. The map arrives as mount data; a step that fetched would be the unauthenticated
 * engine call ADR-107 §2.4 closed (and the staff fork's zero-network gate would fail).
 */
export function StepResidency({ data, onNext, residency }: StepProps) {
  const map = residency?.map ?? null;
  // `undefined` = still loading (no error yet); `null` = could not load (fail-closed).
  const loading = !!residency && residency.map === undefined;
  const stored = residency?.stored;
  const answered = typeof data?.jurisdiction === 'string' ? (data.jurisdiction as string) : '';
  const prefill =
    residency?.prefill && map && map.countries[residency.prefill.toUpperCase()]
      ? residency.prefill.toUpperCase()
      : '';
  const [country, setCountry] = useState<string>(answered || prefill);
  const [busy, setBusy] = useState(false);
  const [closedIn, setClosedIn] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const options = useMemo(() => (map ? countryOptions(map) : []), [map]);

  // ── Read-only: the home exists and cannot change (D1) ──────────────────────────────────
  if (stored) {
    const j = stored.jurisdiction;
    const line = j && map ? consequenceLine(map, j) : stored.zoneLabel ? sameCountryLine(stored.zoneLabel) : null;
    return (
      <div className="obw-step-content">
        <h2 className="obw-title">Data residency</h2>
        <div className="obw-form-group">
          <p className="obw-form-label">{RESIDENCY_LABEL}</p>
          <p className="obw-residency-readonly" data-testid="residency-readonly-country">
            {j ? countryName(j) : '—'}
          </p>
          {line && <p className="obw-residency-consequence" data-testid="residency-consequence">{line}</p>}
        </div>
        <button type="button" className="obw-btn-primary" onClick={() => onNext({})}>
          Continue →
        </button>
      </div>
    );
  }

  // ── Editable: the answer the mint will use ─────────────────────────────────────────────
  const derived = country && map ? zoneFor(map, country) : null;
  const line = country && map ? consequenceLine(map, country) : null;
  const working = residency?.workingZone;
  const notice = derived && working && derived.zone !== working.code ? createdInZoneNotice(derived.label) : null;

  async function submit() {
    if (!derived) return;
    setBusy(true);
    setClosedIn(null);
    setFailed(false);
    try {
      const outcome = residency?.confirm ? await residency.confirm(country, derived.zone) : { ok: true as const };
      if (outcome.ok) {
        // `zoneLabel` rides along so a later refusal can NAME the zone (I9) without the map.
        onNext({ jurisdiction: country, zone: derived.zone, zoneLabel: derived.label, ...(outcome.seed ?? {}) });
        return;
      }
      if (outcome.reason === 'gate_closed') setClosedIn(derived.label);
      else setFailed(true);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="obw-step-content">
      <h2 className="obw-title">Data residency</h2>
      <div className="obw-form-group">
        <label className="obw-form-label" htmlFor="obw-residency-country">{RESIDENCY_LABEL}</label>
        <p className="obw-residency-helper">{RESIDENCY_HELPER}</p>
        <select
          id="obw-residency-country"
          className="obw-form-input"
          value={country}
          disabled={!map || busy}
          aria-busy={loading || undefined}
          onChange={(e) => {
            setCountry(e.target.value);
            setClosedIn(null);
            setFailed(false);
          }}
        >
          <option value="">— Select —</option>
          {options.map((o) => (
            <option key={o.code} value={o.code}>{o.name}</option>
          ))}
        </select>
        {line && <p className="obw-residency-consequence" data-testid="residency-consequence">{line}</p>}
        {notice && <p className="obw-residency-notice" role="note" data-testid="residency-zone-notice">{notice}</p>}
      </div>

      {/* No copy is invented here: I9 names none for a failure, so both failure states use the
          platform's existing server-error sentence (signup-api SIGNUP_ERROR_MESSAGES). */}
      {!map && !loading && (
        <div className="portal-wizard-error" role="alert">
          Something went wrong on our side. Please try again in a moment.
        </div>
      )}
      {closedIn && (
        <div className="portal-wizard-error" role="alert" data-testid="residency-gate-closed">
          {closedGateLine(closedIn)}
        </div>
      )}
      {failed && (
        <div className="portal-wizard-error" role="alert">
          Something went wrong on our side. Please try again in a moment.
        </div>
      )}

      <button
        type="button"
        className="obw-btn-primary"
        disabled={!derived || busy}
        onClick={() => void submit()}
      >
        {busy ? 'Checking…' : 'Continue →'}
      </button>
    </div>
  );
}
